import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { toast } from 'sonner';
import {
  Save,
  Loader2,
  ArrowLeft,
  Clock,
  Search,
  Home,
  User,
  Shield,
  GraduationCap,
  Handshake,
  ChevronDown,
  ChevronUp,
  Image,
  Upload,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAGES = [
  { key: 'home', label: 'Homepage', icon: Home },
  { key: 'founder', label: 'Founder Page', icon: User },
  { key: 'safety', label: 'Safety & Standards', icon: Shield },
  { key: 'programs', label: 'Programs & Pathways', icon: GraduationCap },
  { key: 'partnerWithUs', label: 'Partner With Us', icon: Handshake },
];

const SEOEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const [uploading, setUploading] = useState({});
  const [expandedPages, setExpandedPages] = useState({ home: true });

  const [config, setConfig] = useState({
    pages: {},
    updated_at: null,
  });

  const updateConfig = useCallback((updater) => {
    setConfig(prev => typeof updater === 'function' ? updater(prev) : updater);
    setIsDirty(true);
    isDirtyRef.current = true;
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleNavigateBack = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
    navigate(-1);
  };

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/seo-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setIsDirty(false);
        isDirtyRef.current = false;
      }
    } catch (error) {
      toast.error('Failed to load SEO config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/seo-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(config),
      });
      if (res.ok) {
        toast.success('SEO settings saved');
        setIsDirty(false);
        isDirtyRef.current = false;
        fetchConfig();
      } else throw new Error();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updatePageSEO = (pageKey, field, value) => {
    updateConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageKey]: {
          ...prev.pages[pageKey],
          [field]: value
        }
      }
    }));
  };

  const handleOGImageUpload = async (file, pageKey) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are allowed');
      return;
    }

    setUploading(prev => ({ ...prev, [pageKey]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'seo_og_image');
      const res = await fetch(`${BACKEND_URL}/api/landing/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        updatePageSEO(pageKey, 'og_image_url', data.image_url);
        toast.success('OG image uploaded');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [pageKey]: false }));
    }
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${url}`;
    }
    return `${BACKEND_URL}${url}`;
  };

  const togglePage = (key) => {
    setExpandedPages(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto p-6" data-testid="seo-editor">
      {/* Header */}
      <div className="flex items-center justify-between sticky top-0 bg-slate-50 py-4 -mx-6 px-6 z-10 border-b">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleNavigateBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">SEO Settings</h1>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Page titles, descriptions, and social sharing images</span>
              {config.updated_at && (
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="w-3 h-3" />
                  Updated {new Date(config.updated_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="save-seo-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </div>

      {/* Page SEO Sections */}
      {PAGES.map(page => {
        const IconComponent = page.icon;
        const pageData = config.pages?.[page.key] || {};
        const ogImageUrl = getFileUrl(pageData.og_image_url);

        return (
          <Collapsible key={page.key} open={expandedPages[page.key]} onOpenChange={() => togglePage(page.key)}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <IconComponent className="w-5 h-5 text-primary" />
                      {page.label}
                    </div>
                    {expandedPages[page.key] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-0">
                  <div>
                    <Label>Page Title</Label>
                    <Input 
                      value={pageData.title || ''} 
                      onChange={e => updatePageSEO(page.key, 'title', e.target.value)} 
                      placeholder={`${page.label} | Eswatini Seals`}
                    />
                    <p className="text-xs text-slate-400 mt-1">Recommended: 50-60 characters</p>
                  </div>
                  <div>
                    <Label>Meta Description</Label>
                    <Textarea 
                      value={pageData.description || ''} 
                      onChange={e => updatePageSEO(page.key, 'description', e.target.value)} 
                      rows={3}
                      placeholder="Brief description of this page..."
                    />
                    <p className="text-xs text-slate-400 mt-1">Recommended: 150-160 characters</p>
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Image className="w-4 h-4 text-slate-400" />
                      OG Image (Social Sharing)
                    </Label>
                    <div className="flex items-start gap-4 mt-2">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        id={`og-${page.key}`}
                        onChange={e => e.target.files?.[0] && handleOGImageUpload(e.target.files[0], page.key)}
                      />
                      <label
                        htmlFor={`og-${page.key}`}
                        className="w-48 h-28 rounded-lg border-2 border-dashed border-slate-300 hover:border-primary cursor-pointer overflow-hidden bg-slate-50 flex items-center justify-center"
                      >
                        {uploading[page.key] ? (
                          <Loader2 className="w-6 h-6 animate-spin" />
                        ) : ogImageUrl ? (
                          <img src={ogImageUrl} alt="OG preview" className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex flex-col items-center text-slate-400">
                            <Upload className="w-6 h-6 mb-1" />
                            <span className="text-xs">Upload OG Image</span>
                          </div>
                        )}
                      </label>
                      <div className="text-xs text-slate-400">
                        <p>Recommended: 1200x630px</p>
                        <p>Shown when shared on social media</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        );
      })}

      {/* Bottom Save */}
      <div className="flex justify-end pb-8">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-primary">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save SEO Settings
        </Button>
      </div>
    </div>
  );
};

export default SEOEditor;
